import React from "react";
import { Trash2 } from "lucide-react";

import QtySelector from "../QtySelector";
import { useProductCartCard } from "../../hooks/useProductCartCard";
import { resolveImageUrl } from "../../utilities/Common";
import fallbackImg from "../../images/broken-watch.png";

interface CartDrawerItemProps {
  product: any;
  selected: boolean;
  isQtyUpdating: boolean;
  onSelect: (mappingCode: string, selected: boolean) => void;
  onQuantityChange: (mappingCode: string, qty: number) => void;
  onRemoved: () => void;
  /** Opens the product page — the drawer closes first (passed in by CartDrawer). */
  onOpenProduct: () => void;
}

/**
 * One line in the cart drawer.
 *
 * Purpose-built for the narrow drawer rather than reusing ProductCartCard —
 * that one is a full-width page card (absolutely-positioned action bar,
 * nested Bootstrap .row/.col grids sized off the viewport, not the drawer)
 * and could only be squeezed in here with layout overrides that kept
 * breaking. This is a plain flex row: thumb, details, quantity, price.
 */
const CartDrawerItem: React.FC<CartDrawerItemProps> = ({
  product,
  selected,
  isQtyUpdating,
  onSelect,
  onQuantityChange,
  onRemoved,
  onOpenProduct,
}) => {
  const cart = useProductCartCard({ product, onRemoved });

  const price = Number(product.price) || 0;
  const mrp = Number(product.mrp) || 0;
  const qty = Number(product.quantity) || 1;
  const lineTotal = price * qty;
  const isStockOut = Boolean(product.isStockOut);
  const imageUrl = resolveImageUrl(product.image) || String(fallbackImg);

  return (
    <div className={`cd-item${isStockOut ? " cd-item--oos" : ""}`}>
      {cart.isBlurLoading && (
        <div className="cd-item__busy">
          <span className="cd-item__spinner" />
        </div>
      )}

      <input
        type="checkbox"
        className="form-check-input cd-item__check"
        checked={selected}
        onChange={(e) => onSelect(cart.mappingCode, e.target.checked)}
        aria-label={`Select ${product.product}`}
      />

      <button type="button" className="cd-item__thumb" onClick={onOpenProduct}>
        <img src={imageUrl} alt={product.product} loading="lazy" />
      </button>

      <div className="cd-item__body">
        <div className="cd-item__head">
          <button type="button" className="cd-item__name" onClick={onOpenProduct}>
            {product.product}
          </button>
          <button
            type="button"
            className="cd-item__remove"
            onClick={cart.handleRemove}
            aria-label={`Remove ${product.product}`}
          >
            <Trash2 size={15} />
          </button>
        </div>

        {/* Size/variation is hidden once the line is out of stock — there's
            nothing to pick, and the out-of-stock notice is the only thing
            that matters on the row at that point. */}
        {!isStockOut && product.variationName && (
          <span className="cd-item__meta">{product.variationName}</span>
        )}

        {isStockOut && (
          <span className="cd-item__oos-note">Out of stock — remove it to continue</span>
        )}

        <div className="cd-item__foot">
          <div className="cd-item__price">
            <span className="cd-item__price-now">₹{lineTotal.toLocaleString("en-IN")}</span>
            {mrp > price && (
              <span className="cd-item__price-mrp">₹{(mrp * qty).toLocaleString("en-IN")}</span>
            )}
          </div>

          {!isStockOut && (
            <QtySelector
              value={qty}
              availableStock={Number(product.availableStock) || 0}
              disabled={isQtyUpdating}
              onChange={(newQty) => onQuantityChange(cart.mappingCode, newQty)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDrawerItem;
